import React from 'react';
import { Plus, ChevronLeft, ChevronRight, ChevronDown, LayoutPanelTop, GripVertical, FileText, Folder, FolderOpen, Trash2 } from 'lucide-react';
import {
    DndContext, closestCenter, DragOverlay, defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableSlide from './SortableSlide';

/**
 * PPTOutlineSidebar — extracted from PPTCustomization.jsx (Phase 8 refactor).
 * Left sidebar: collapsible section tree with drag-and-drop slides.
 * Zero logic changes. All state/handlers passed as props.
 */
const PPTOutlineSidebar = ({
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    sections,
    setSections,
    activeSectionId,
    setActiveSectionId,
    activeTitleId,
    expandedSections,
    toggleSection,
    handleAddSection,
    handleAddTitle,
    handleUpdateSection,
    selectTitle,
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    activeDragId,
    setIsCreatingNewSlide,
    setIsCreatingNewSection,
    handleDeleteTitle,
    handleDeleteSection
}) => (
    <aside className={`ppt-sidebar-nav ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header-row">
            {!isSidebarCollapsed && <h3 className="animate-fade-in">Proposal Structure</h3>}
            <div className="sidebar-header-actions">
                {!isSidebarCollapsed && (
                    <button className="btn-icon-add" onClick={handleAddSection} title="Add Section">
                        <Plus size={16} />
                    </button>
                )}
                <button
                    className="btn-sidebar-toggle"
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                </button>
            </div>
        </div>

        {!isSidebarCollapsed && (
            <div className="outline-scroll-area animate-fade-in">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    {sections.map(section => (
                        <div key={section.id} className="outline-section">
                            <div
                                className={`section-row ${activeSectionId === section.id ? 'active' : ''}`}
                                onClick={() => {
                                    toggleSection(section.id);
                                    setActiveSectionId(section.id);
                                    if (section.titles && section.titles.length > 0) {
                                        selectTitle(section.id, section.titles[0].id);
                                        setIsCreatingNewSlide(false);
                                        setIsCreatingNewSection(false);
                                    }
                                }}
                                style={{ padding: '0.25rem 0.5rem', marginBottom: '2px' }}
                            >
                                <div className="section-chevron" style={{ opacity: 0.5 }}>
                                    {expandedSections.includes(section.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </div>
                                <div className="section-icon" style={{ color: '#F59E0B' }}>
                                    {expandedSections.includes(section.id) ? <FolderOpen size={14} /> : <Folder size={14} />}
                                </div>
                                <input
                                    className="section-rename-inline"
                                    value={section.name}
                                    onChange={(e) => setSections(sections.map(sec => sec.id === section.id ? { ...sec, name: e.target.value } : sec))}
                                    onBlur={(e) => handleUpdateSection(section.id, e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}
                                />
                                {handleDeleteSection && (
                                    <button
                                        className="section-delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteSection(section.id);
                                        }}
                                        title="Delete Section"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                )}
                            </div>

                            {expandedSections.includes(section.id) && (
                                <div className="slide-items">
                                    <SortableContext items={section.titles.map(t => t.id)} strategy={verticalListSortingStrategy}>
                                        {section.titles.map(title => (
                                            <SortableSlide
                                                key={title.id}
                                                title={title}
                                                sectionId={section.id}
                                                activeTitleId={activeTitleId}
                                                onSelect={selectTitle}
                                                handleDeleteTitle={handleDeleteTitle}
                                            />
                                        ))}
                                    </SortableContext>
                                    <button
                                        className="add-slide-btn"
                                        onClick={(e) => { e.stopPropagation(); handleAddTitle(section.id); }}
                                        style={{ border: 'none', padding: '4px 8px', marginTop: 0, justifyContent: 'flex-start', color: '#64748B' }}
                                        onMouseEnter={e => e.currentTarget.style.color = '#FF5526'}
                                        onMouseLeave={e => e.currentTarget.style.color = '#64748B'}
                                    >
                                        <Plus size={12} />
                                        <span style={{ fontSize: '0.7rem' }}>Add Slide</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    <DragOverlay dropAnimation={{ sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.5' } } }) }}>
                        {activeDragId ? (
                            <div className="slide-row active overlay">
                                <div className="slide-left-group">
                                    <div className="drag-handle"><GripVertical size={14} className="grip-icon" /></div>
                                    <div className="slide-icon"><FileText size={14} /></div>
                                    <span className="slide-label">
                                        {sections.flatMap(s => s.titles).find(t => t.id === activeDragId)?.text || 'Untitled Slide'}
                                    </span>
                                </div>
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </div>
        )}

        {isSidebarCollapsed && (
            <div className="collapsed-sidebar-icons animate-fade-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', marginTop: '16px', width: '100%', overflowY: 'auto', overflowX: 'hidden', paddingBottom: '16px' }}>
                {sections.map(section => (
                    <button
                        key={section.id}
                        className={`collapsed-section-icon-btn ${activeSectionId === section.id ? 'active' : ''}`}
                        onClick={() => {
                            if (!expandedSections.includes(section.id)) {
                                toggleSection(section.id);
                            }
                            setActiveSectionId(section.id);
                            if (section.titles && section.titles.length > 0) {
                                selectTitle(section.id, section.titles[0].id);
                                setIsCreatingNewSlide(false);
                                setIsCreatingNewSection(false);
                            }
                        }}
                        title={section.name}
                        style={{
                            background: activeSectionId === section.id ? '#FFF1EC' : 'transparent',
                            color: activeSectionId === section.id ? '#E9471D' : '#64748B',
                            border: 'none',
                            borderRadius: '8px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: activeSectionId === section.id ? '0 2px 8px rgba(37, 99, 235, 0.15)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                            if (activeSectionId !== section.id) {
                                e.currentTarget.style.background = '#F1F5F9';
                                e.currentTarget.style.color = '#334155';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (activeSectionId !== section.id) {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#64748B';
                            }
                        }}
                    >
                        <LayoutPanelTop size={20} />
                    </button>
                ))}
            </div>
        )}
    </aside>
);

export default PPTOutlineSidebar;
